import * as L from "leaflet";
import { Pl3xMap } from "../Pl3xMap";
import SidebarControl from "./SidebarControl";
import { createSVGIcon, toPoint } from "../util/Util";
import "../svg/marker_add.svg";

interface PlaceMarkerMessage {
    type: "pl3xmap:place-marker";
    marker: {
        x: number;
        // Height of the clicked block, or null when the block palette has no
        // value for that spot yet — the parent decides on a fallback.
        y: number | null;
        z: number;
        world: string;
    };
}

/**
 * Sidebar button that starts "marker placement" mode: the button hides, the pin
 * icon follows the cursor, and the next click on the map sends its coordinates
 * to the parent window (which opens the create-marker modal) and ends the mode.
 */
export class MarkerPlacementControl {
    private readonly _pl3xmap: Pl3xMap;
    private readonly _button: HTMLButtonElement;
    private _ghost?: HTMLDivElement;
    private _placing: boolean = false;

    constructor(pl3xmap: Pl3xMap, sidebar: SidebarControl) {
        this._pl3xmap = pl3xmap;

        this._button = L.DomUtil.create('button');
        this._button.type = 'button';
        this._button.id = 'sidebar__add-marker';
        this._button.title = 'Додати маркер';
        this._button.setAttribute('aria-label', 'Додати маркер');
        this._button.appendChild(createSVGIcon('marker_add'));
        this._button.addEventListener('click', (): void => this.start());

        sidebar.addButton(this._button);
    }

    get button(): HTMLButtonElement {
        return this._button;
    }

    /** True while the next map click is reserved for placing a marker. */
    get isPlacing(): boolean {
        return this._placing;
    }

    /** Hide the button and attach the pin to the cursor. */
    public start(): void {
        if (this._placing) {
            return;
        }

        this._placing = true;
        this._button.hidden = true;

        // Outer element tracks the cursor, inner one carries the anchor offset
        // and the swing animation, so the two transforms never fight.
        this._ghost = L.DomUtil.create('div', 'marker-ghost', document.body);
        const icon: HTMLDivElement = L.DomUtil.create('div', 'marker-ghost__icon', this._ghost);
        icon.appendChild(createSVGIcon('marker_add'));
        // Stays invisible until the first mouse move gives it a position.
        this._ghost.style.visibility = 'hidden';

        this._pl3xmap.map.getContainer().classList.add('map--placing-marker');

        document.addEventListener('mousemove', this.onPointerMove);
        document.addEventListener('keydown', this.onKeyDown);
        this._pl3xmap.map.on('click', this.onMapClick);
        this._pl3xmap.map.on('contextmenu', this.cancel);
    }

    /** Drop the cursor pin and bring the button back. */
    public stop(): void {
        if (!this._placing) {
            return;
        }

        this._placing = false;
        this._button.hidden = false;

        this._ghost?.remove();
        this._ghost = undefined;

        this._pl3xmap.map.getContainer().classList.remove('map--placing-marker');

        document.removeEventListener('mousemove', this.onPointerMove);
        document.removeEventListener('keydown', this.onKeyDown);
        this._pl3xmap.map.off('click', this.onMapClick);
        this._pl3xmap.map.off('contextmenu', this.cancel);
    }

    private cancel = (): void => this.stop();

    private onPointerMove = (e: MouseEvent): void => {
        if (!this._ghost) {
            return;
        }

        this._ghost.style.visibility = 'visible';
        this._ghost.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
    };

    private onKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
            this.stop();
        }
    };

    private onMapClick = (e: L.LeafletMouseEvent): void => {
        const point: L.PointTuple = toPoint(e.latlng);
        // Same rounding as CoordsControl, so the sent block matches the readout.
        const x: number = Math.round(point[0]) - 1;
        const z: number = Math.round(point[1]) - 1;

        // Y comes from the hovered block's height map; only trust it when the
        // coords readout is still on the very block that was clicked.
        const coords = this._pl3xmap.controlManager.coordsControl;
        const y: number | null = coords?.x === x && coords?.z === z ? coords.y ?? null : null;

        this.stop();
        this.sendToParent(x, y, z);
    };

    private sendToParent(x: number, y: number | null, z: number): void {
        const message: PlaceMarkerMessage = {
            type: "pl3xmap:place-marker",
            marker: {
                x,
                y,
                z,
                world: this._pl3xmap.worldManager.currentWorld?.name || 'world',
            },
        };

        if (window.parent !== window) {
            window.parent.postMessage(message, "*");
        }

        window.dispatchEvent(new CustomEvent("pl3xmap:place-marker", { detail: message.marker }));
    }
}
